import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, FileUp, Loader2, X, Paperclip } from "lucide-react";
import { Reveal } from "@/components/motion";
import { api, usePageMeta } from "@/lib/api";

const SERVICES = ["ESG Strategy", "BRSR", "GHG Accounting", "Climate Risk", "Biodiversity", "D&I", "ESG Policy", "Responsible Investment", "Training", "Other"];
const ORG_TYPES = ["Listed Company", "Private Company", "Financial Institution", "Investor / Fund", "SME", "Infrastructure / Project", "NGO / Development Organisation", "Other"];
const INDUSTRIES = ["Financial Services", "Real Estate & Infrastructure", "Manufacturing", "Technology", "Consumer & FMCG", "Energy", "Other"];
const MATURITY = ["Just starting", "Developing", "Integrated", "Advanced", "Not sure"];
const CONSULT_TYPE = ["Video call", "Phone call", "In-person meeting", "Email exchange"];

const ALLOWED = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "ppt", "pptx", "png", "jpg", "jpeg", "webp"];
const MAX_MB = 10;

const inputCls = "w-full bg-white/70 border rule-light px-4 py-3.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest transition-colors";

export default function ContactPage() {
  usePageMeta("Book an ESG Consultation | SarvaRitam", "Request an ESG consultation with SarvaRitam — strategy, BRSR, carbon, climate risk, policies, responsible investment or training.");
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", company: "", designation: "", phone: "",
    orgType: "", industry: params.get("industry") || "",
    service: params.get("service") || "", maturity: "",
    consultationType: "Video call", message: "",
  });
  const [files, setFiles] = useState([]); // [{id, filename, size}]
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadFiles = async (list) => {
    setUploadError("");
    for (const f of list) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { setUploadError(`"${f.name}" — file type not supported.`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { setUploadError(`"${f.name}" exceeds the ${MAX_MB} MB limit.`); continue; }
      if (files.length + 1 > 5) { setUploadError("Up to 5 attachments per request."); break; }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((fs) => [...fs, res.data]);
      } catch (e) {
        setUploadError(e?.response?.data?.detail || "Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "A valid email is required";
    if (!form.message.trim()) e.message = "Tell us briefly about your requirement";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await api.post("/consultations", { ...form, attachments: files.map((f) => f.id) });
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
    }
  };

  const fmtSize = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

  if (status === "success") {
    return (
      <section className="bg-charcoal text-offwhite min-h-screen flex items-center justify-center pt-32 pb-24 relative grain" data-testid="contact-success">
        <div className="wrap max-w-xl text-center relative">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
            <span className="w-16 h-16 rounded-full bg-gold text-charcoal flex items-center justify-center mx-auto mb-8"><Check size={30} /></span>
          </motion.div>
          <h1 className="h-display font-semibold text-3xl sm:text-4xl">Thank you. Your request has been received.</h1>
          <p className="text-white/80 text-[15px] leading-relaxed mt-5">
            A SarvaRitam advisor will review your requirement and respond directly. If you shared attachments, they're safely stored with your request.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a href="/" className="btn-ghost-light px-8 py-4 justify-center" data-testid="contact-success-home">Back to home</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-forest text-offwhite relative grain overflow-hidden pt-40 pb-24" data-testid="contact-page">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: "radial-gradient(50% 60% at 85% 5%, rgba(198,161,91,0.45), transparent 65%)" }} />
      <div className="wrap relative grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <p className="eyebrow text-gold flex items-center gap-3 mb-6">Contact <span className="h-px w-14 bg-gold/50" /></p>
          <h1 className="h-display font-semibold text-[32px] sm:text-5xl leading-[1.05]">
            Let's solve your <span className="serif-it text-gold">ESG & sustainability challenge.</span>
          </h1>
          <p className="text-white/85 text-[15px] leading-relaxed mt-6 max-w-sm">
            Tell us where your organisation stands and what you're trying to achieve. We'll suggest the clearest next step — honestly, and without obligation.
          </p>
          <img src="/brand/lockup-services-white.png" alt="SarvaRitam™ — ESG Strategy · BRSR · GHG Accounting · Climate Risk · Biodiversity · D&I" className="h-24 w-auto mt-10" data-testid="contact-brand-lockup" draggable="false" />
          <div className="mt-10 space-y-5">
            {[
              ["01", "Share your context", "Five minutes of your time — the more specific, the better our first response."],
              ["02", "We review internally", "Your request goes to an advisor matched to your topic and sector."],
              ["03", "A direct reply", "You hear back from a person, with a suggested approach and next step."],
            ].map(([n, t, d]) => (
              <Reveal key={n} delay={n === "02" ? 0.06 : n === "03" ? 0.12 : 0}>
                <div className="flex gap-4">
                  <span className="font-mono text-[11px] text-gold mt-1">{n}</span>
                  <div>
                    <p className="font-display font-semibold text-[15px]">{t}</p>
                    <p className="text-white/75 text-[13px] mt-1 leading-relaxed">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-white/15 space-y-3.5 text-[13px]" data-testid="contact-page-details">
            <a href="mailto:sarvaritam@gmail.com" className="block u-sweep w-fit font-semibold text-white/92 hover:text-gold transition-colors" data-testid="contact-page-email-link">sarvaritam@gmail.com</a>
            <a href="tel:+919228411223" className="block u-sweep w-fit font-semibold text-white/92 hover:text-gold transition-colors" data-testid="contact-page-phone-link">+91 92284 11223</a>
            <p className="text-white/75 leading-relaxed" data-testid="contact-page-address">Lower Parel, Mumbai,<br />Maharashtra 400013, India</p>
          </div>
        </div>

        <div className="lg:col-span-8">
          <form onSubmit={submit} noValidate className="bg-offwhite text-charcoal p-7 sm:p-12 relative" data-testid="consultation-form">
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { k: "name", label: "Name *", ph: "Your full name" },
                { k: "email", label: "Email *", ph: "you@company.com", type: "email" },
                { k: "company", label: "Company", ph: "Organisation name" },
                { k: "designation", label: "Designation", ph: "Your role" },
                { k: "phone", label: "Phone", ph: "+91 …" },
              ].map((f) => (
                <div key={f.k} className={f.k === "name" || f.k === "email" ? "" : ""}>
                  <label className="eyebrow text-forest block mb-2" htmlFor={`cf-${f.k}`}>{f.label}</label>
                  <input id={`cf-${f.k}`} type={f.type || "text"} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph} className={inputCls} data-testid={`consultation-${f.k}-input`} />
                  {errors[f.k] && <p className="text-[11.5px] text-red-700 mt-1.5" data-testid={`consultation-${f.k}-error`}>{errors[f.k]}</p>}
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              {[
                { k: "orgType", label: "Organisation Type", opts: ORG_TYPES },
                { k: "industry", label: "Industry", opts: INDUSTRIES },
                { k: "service", label: "Service Required", opts: SERVICES },
                { k: "maturity", label: "Current ESG Maturity", opts: MATURITY },
                { k: "consultationType", label: "Preferred Consultation Type", opts: CONSULT_TYPE },
              ].map((f) => (
                <div key={f.k}>
                  <label className="eyebrow text-forest block mb-2" htmlFor={`cf-${f.k}`}>{f.label}</label>
                  <select id={`cf-${f.k}`} value={form[f.k]} onChange={set(f.k)} className={inputCls} data-testid={`consultation-${f.k}-select`}>
                    <option value="">Select…</option>
                    {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <label className="eyebrow text-forest block mb-2" htmlFor="cf-message">Message *</label>
              <textarea id="cf-message" rows={5} value={form.message} onChange={set("message")} placeholder="Briefly describe your requirement, timeline and any context we should know…" className={inputCls} data-testid="consultation-message-input" />
              {errors.message && <p className="text-[11.5px] text-red-700 mt-1.5" data-testid="consultation-message-error">{errors.message}</p>}
            </div>

            <div className="mt-6">
              <p className="eyebrow text-forest mb-3">Attachments (optional — up to 5, max {MAX_MB} MB each)</p>
              <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="consultation-file-input" aria-label="Attach files" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-3 border border-dashed border-forest/40 hover:border-forest px-5 py-4 w-full text-left transition-colors" data-testid="consultation-attach-button">
                {uploading ? <Loader2 size={17} className="text-forest animate-spin" /> : <FileUp size={17} className="text-forest" />}
                <span className="text-[13px] font-semibold text-charcoal/70">
                  {uploading ? "Uploading…" : "Attach files — existing ESG reports, BRSR drafts, policies, data"}
                </span>
              </button>
              {uploadError && <p className="text-[12px] text-red-700 mt-2" data-testid="consultation-upload-error">{uploadError}</p>}
              {files.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="consultation-attachment-list">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between bg-forest/[0.06] border rule-light px-4 py-2.5">
                      <span className="flex items-center gap-2.5 text-[13px] font-semibold text-charcoal/75 min-w-0">
                        <Paperclip size={13} className="text-gold shrink-0" /> <span className="truncate">{f.filename}</span>
                        <span className="font-mono text-[10.5px] text-sage shrink-0">{fmtSize(f.size)}</span>
                      </span>
                      <button type="button" onClick={() => setFiles((fs) => fs.filter((x) => x.id !== f.id))} className="text-charcoal/40 hover:text-red-700 p-1" data-testid={`consultation-remove-attachment-${f.id}`} aria-label={`Remove ${f.filename}`}>
                        <X size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {status === "error" && (
              <p className="mt-6 text-[13px] text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="consultation-submit-error">
                Something went wrong sending your request. Please try again, or email us directly.
              </p>
            )}

            <button type="submit" disabled={status === "sending"} className="btn-forest w-full justify-center mt-8 py-4 text-[15px]" data-testid="consultation-submit-button">
              {status === "sending" ? "Sending…" : <>Request Consultation <ArrowUpRight size={16} /></>}
            </button>
            <p className="text-[11.5px] text-charcoal/45 mt-4 leading-relaxed">
              By submitting, you agree that SarvaRitam may contact you about your enquiry. Your information is used only to respond to this request.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
